import React, {
    useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle,
} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, FormControl, InputLabel, Select, MenuItem,
    Typography, Divider, IconButton, FormControlLabel, Switch,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { sanitizeLoginSignInTermsHtml } from 'utils/sanitizeLoginSignInTermsHtml';
import { useSystemModules } from 'hooks/useSystemModules';

/** Same values as Add New Company dialog (CompanyDialog.tsx). */
const LICENSE_TYPES = [
    'Enterprise',
    'BusinessPlus',
    'Business',
    'VerifiedPro',
    'PublicFree',
] as const;

const SSO_SIGNUP_DURATIONS: { value: string; label: string }[] = [
    ...Array.from({ length: 12 }, (_, i) => {
        const n = i + 1;
        return { value: String(n), label: n === 1 ? '1 month' : `${n} months` };
    }),
    { value: 'infinite', label: 'Infinite' },
];

const FALLBACK_MODULES = ['instantupdate.ai', 'automatetest.ai', 'assetsmanagement.ai', 'infralytiqs.com'];

interface SSOProviderForm {
    id: string;
    buttonName: string;
    providerType: string;
    loginPageHashPath: string;
    clientId: string;
    clientSecret: string;
    scopes: string;
    authorizationUrl: string;
    tokenUrl: string;
    prompt: string;
}

type ThemeLogoSlotPayload = { base64: string; contentType: string };

interface DomainRecord {
    _id: string;
    domainKey: string;
    apiBaseUrl: string;
    brandLogo: { name: string; path: string };
    minifiedBrandLogo: { name: string; path: string };
    themeBrandLogos?: { hasLight: boolean; hasDark: boolean } | {
        light?: { contentType: string; dataBase64: string };
        dark?: { contentType: string; dataBase64: string };
    };
    menuKey: string;
    theme?: string;
    uiDictionaryKey?: 'campaignKing' | 'instantUpdate' | 'anamnesis' | 'infralytiqs';
    supportEmail: string;
    contactPhone: string;
    contactEmail: string;
    logLevel: string;
    facebookAppId: string;
    facebookApiVersion: string;
    fbEmbeddedRedirectUri: string;
    googleMapsBrowserJSApiKey: string;
    loginPageHashPath: string;
    loginSignInTermsNotice?: string;
    userSignUpEnabled?: boolean;
    /** When true, SSO login auto-provisions company + admin for unknown users. */
    ssoSignUpEnabled?: boolean;
    ssoSignUp?: {
        licenseType?: string;
        module?: string;
        duration?: string;
    };
    /** When false, the "Manage Plan" sidebar menu is hidden. */
    managePlan?: boolean;
    /** Optional CDN base for published Asset Explorer URLs */
    assetsS3CdnUrl?: string;
    ssoProviders?: SSOProviderForm[];
    infralytiqs?: {
        tenantId?: string;
        siteId?: string;
        dbName?: string;
        captureLocation?: boolean;
    };
    beamOnScreenInfralytiqs?: {
        tenantId?: string;
        siteId?: string;
    };
}

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    domain: DomainRecord | null;
}

const EMPTY_SSO_PROVIDER: SSOProviderForm = {
    id: '', buttonName: '', providerType: 'google', loginPageHashPath: '/#/login',
    clientId: '', clientSecret: '', scopes: 'openid email profile',
    authorizationUrl: '', tokenUrl: '', prompt: '',
};

const EMPTY_QUILL_HTML = '<p><br></p>';

const EMPTY_FORM = {
    domainKey: '',
    apiBaseUrl: '',
    brandLogoName: '',
    brandLogoPath: '',
    minifiedBrandLogoName: '',
    minifiedBrandLogoPath: '',
    menuKey: 'campaignKing',
    theme: 'DEFAULT' as 'DEFAULT' | 'professional',
    uiDictionaryKey: '' as '' | 'campaignKing' | 'instantUpdate' | 'anamnesis' | 'infralytiqs',
    supportEmail: '',
    contactPhone: '',
    contactEmail: '',
    logLevel: 'info',
    facebookAppId: '',
    facebookApiVersion: '',
    fbEmbeddedRedirectUri: '',
    googleMapsBrowserJSApiKey: '',
    loginPageHashPath: '/#/login',
    loginSignInTermsNotice: EMPTY_QUILL_HTML,
    userSignUpEnabled: true,
    ssoSignUpEnabled: false,
    ssoSignUpLicenseType: '',
    ssoSignUpModule: '',
    ssoSignUpDuration: '',
    managePlan: true,
    infralytiqsTenantId: '',
    infralytiqsSiteId: '',
    infralytiqsDbName: '',
    infralytiqsCaptureLocation: false,
    beamOnScreenTenantId: 'beamOnScreen',
    beamOnScreenSiteId: 'screenPlayBacks',
    assetsS3CdnUrl: '',
};

const INFRALYTIQS_DB_NAME_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,62}$/;
const THEME_LOGO_MAX_BYTES = 600_000;
const ACCEPT_IMAGE = 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml';

const FIND_DOM_NODE_DEPRECATED = 'findDOMNode is deprecated';

let findDomNodeConsoleFilterInstalled = false;
function ensureFindDomNodeConsoleFilter(): void {
    if (findDomNodeConsoleFilterInstalled || typeof window === 'undefined') return;
    findDomNodeConsoleFilterInstalled = true;
    const previous = console.error;
    console.error = (...args: unknown[]) => {
        const suppress = args.some(
            (a) => typeof a === 'string' && a.includes(FIND_DOM_NODE_DEPRECATED),
        );
        if (suppress) return;
        previous.apply(console, args as []);
    };
}

const QuillWrapper = forwardRef<ReactQuill, React.ComponentProps<typeof ReactQuill>>((props, ref) => {
    const quillRef = useRef<ReactQuill>(null);
    useImperativeHandle(ref, () => quillRef.current as ReactQuill);
    return <ReactQuill ref={quillRef} {...props} />;
});
QuillWrapper.displayName = 'QuillWrapper';

function escapeHtmlPlain(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function bodyHasText(html: string): boolean {
    const plain = html
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return plain.length > 0;
}

/** Normalize stored value (plain legacy or HTML) for the Quill editor. */
function storedToQuillHtml(stored: string): string {
    const t = stored.trim();
    if (!t) return EMPTY_QUILL_HTML;
    const safe = sanitizeLoginSignInTermsHtml(t);
    const st = safe.trim();
    if (!st) return EMPTY_QUILL_HTML;
    if (/^<[a-z]/i.test(st)) return safe;
    return `<p>${escapeHtmlPlain(st)}</p>`;
}

function quillHtmlToStored(html: string): string {
    if (!bodyHasText(html)) return '';
    return sanitizeLoginSignInTermsHtml(html).trim();
}

type LogoSlotAction = 'unchanged' | 'replace' | 'clear';

const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
            const s = r.result as string;
            const i = s.indexOf(',');
            resolve(i >= 0 ? s.slice(i + 1) : s);
        };
        r.onerror = () => reject(new Error('Failed to read file'));
        r.readAsDataURL(file);
    });

export const DomainDialog: React.FC<Props> = ({ open, onClose, onSave, domain }) => {
    ensureFindDomNodeConsoleFilter();
    const isEdit = !!domain;
    const [form, setForm] = useState(EMPTY_FORM);
    const [loginTermsEditorReady, setLoginTermsEditorReady] = useState(false);
    const [ssoProviders, setSsoProviders] = useState<SSOProviderForm[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const { modules, loading: modulesLoading } = useSystemModules(open);
    const availableModules = modules.length > 0 ? modules : FALLBACK_MODULES;
    const [lightAction, setLightAction] = useState<LogoSlotAction>('unchanged');
    const [darkAction, setDarkAction] = useState<LogoSlotAction>('unchanged');
    const [lightFile, setLightFile] = useState<File | null>(null);
    const [darkFile, setDarkFile] = useState<File | null>(null);
    const [lightPreview, setLightPreview] = useState<string | null>(null);
    const [darkPreview, setDarkPreview] = useState<string | null>(null);
    const lightInputRef = useRef<HTMLInputElement | null>(null);
    const darkInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!open) {
            setLoginTermsEditorReady(false);
            return;
        }
        setLoginTermsEditorReady(true);
        return () => setLoginTermsEditorReady(false);
    }, [open]);

    const loginTermsQuillModules = useMemo(
        () => ({
            toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link'],
                ['clean'],
            ],
        }),
        [],
    );

    const loginTermsQuillFormats = useMemo(
        () => ['bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link'],
        [],
    );

    useEffect(() => {
        if (!open) return;

        if (domain) {
            setForm({
                domainKey: domain.domainKey || '',
                apiBaseUrl: domain.apiBaseUrl || '',
                brandLogoName: domain.brandLogo?.name || '',
                brandLogoPath: domain.brandLogo?.path || '',
                minifiedBrandLogoName: domain.minifiedBrandLogo?.name || '',
                minifiedBrandLogoPath: domain.minifiedBrandLogo?.path || '',
                menuKey: domain.menuKey || 'campaignKing',
                theme: domain.theme === 'professional' ? 'professional' : 'DEFAULT',
                uiDictionaryKey:
                    domain.uiDictionaryKey === 'campaignKing'
                    || domain.uiDictionaryKey === 'instantUpdate'
                    || domain.uiDictionaryKey === 'anamnesis'
                    || domain.uiDictionaryKey === 'infralytiqs'
                        ? domain.uiDictionaryKey
                        : '',
                supportEmail: domain.supportEmail || '',
                contactPhone: domain.contactPhone || '',
                contactEmail: domain.contactEmail || '',
                logLevel: domain.logLevel || 'info',
                facebookAppId: domain.facebookAppId || '',
                facebookApiVersion: domain.facebookApiVersion || '',
                fbEmbeddedRedirectUri: domain.fbEmbeddedRedirectUri || '',
                googleMapsBrowserJSApiKey: domain.googleMapsBrowserJSApiKey || '',
                loginPageHashPath: domain.loginPageHashPath || '/#/login',
                loginSignInTermsNotice: storedToQuillHtml(domain.loginSignInTermsNotice ?? ''),
                userSignUpEnabled: domain.userSignUpEnabled !== false,
                ssoSignUpEnabled: domain.ssoSignUpEnabled === true,
                ssoSignUpLicenseType: domain.ssoSignUp?.licenseType || '',
                ssoSignUpModule: domain.ssoSignUp?.module || '',
                ssoSignUpDuration: domain.ssoSignUp?.duration || '',
                managePlan: domain.managePlan !== false,
                infralytiqsTenantId: domain.infralytiqs?.tenantId || '',
                infralytiqsSiteId: domain.infralytiqs?.siteId || '',
                infralytiqsDbName: domain.infralytiqs?.dbName || '',
                infralytiqsCaptureLocation: !!domain.infralytiqs?.captureLocation,
                beamOnScreenTenantId: domain.beamOnScreenInfralytiqs?.tenantId || '',
                beamOnScreenSiteId: domain.beamOnScreenInfralytiqs?.siteId || '',
                assetsS3CdnUrl: domain.assetsS3CdnUrl ?? '',
            });
            setSsoProviders(domain.ssoProviders?.map(p => ({ ...EMPTY_SSO_PROVIDER, ...p })) || []);
        } else {
            setForm(EMPTY_FORM);
            setSsoProviders([]);
        }
        setLightAction('unchanged');
        setDarkAction('unchanged');
        setLightFile(null);
        setDarkFile(null);
        setLightPreview(null);
        setDarkPreview(null);
        if (lightInputRef.current) lightInputRef.current.value = '';
        if (darkInputRef.current) darkInputRef.current.value = '';
        setError('');

        if (!domain?._id) return;

        let cancelled = false;
        (async () => {
            try {
                const r = await apiClient.get(`/domain/${domain._id}`);
                if (cancelled) return;
                const t = r.data?.themeBrandLogos as Record<string, { contentType: string; dataBase64: string }> | undefined;
                if (t?.light?.dataBase64) {
                    setLightPreview(`data:${t.light.contentType};base64,${t.light.dataBase64}`);
                }
                if (t?.dark?.dataBase64) {
                    setDarkPreview(`data:${t.dark.contentType};base64,${t.dark.dataBase64}`);
                }
            } catch (e: any) {
                Logger.error('DomainDialog', `Failed to load domain logos: ${e}`);
            }
        })();

        return () => { cancelled = true; };
    }, [domain, open]);

    const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSelectChange = (field: string) => (e: any) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSsoChange = (idx: number, field: keyof SSOProviderForm) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setSsoProviders(prev => prev.map((p, i) => i === idx ? { ...p, [field]: e.target.value } : p));
        };

    const handleSsoSelectChange = (idx: number, field: keyof SSOProviderForm) =>
        (e: any) => {
            setSsoProviders(prev => prev.map((p, i) => i === idx ? { ...p, [field]: e.target.value } : p));
        };

    const addSsoProvider = () => setSsoProviders(prev => [...prev, { ...EMPTY_SSO_PROVIDER }]);
    const removeSsoProvider = (idx: number) => setSsoProviders(prev => prev.filter((_, i) => i !== idx));

    const validatePickedFile = (file: File): string | null => {
        if (file.size > THEME_LOGO_MAX_BYTES) {
            return `Image must be at most ${Math.round(THEME_LOGO_MAX_BYTES / 1000)} KB`;
        }
        const ok = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'].includes(file.type);
        if (!ok) return 'Use PNG, JPEG, GIF, WebP, or SVG';
        return null;
    };

    const onPickLight: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const msg = validatePickedFile(file);
        if (msg) { setError(msg); e.target.value = ''; return; }
        setError('');
        setLightFile(file);
        setLightAction('replace');
        setLightPreview(URL.createObjectURL(file));
    };

    const onPickDark: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const msg = validatePickedFile(file);
        if (msg) { setError(msg); e.target.value = ''; return; }
        setError('');
        setDarkFile(file);
        setDarkAction('replace');
        setDarkPreview(URL.createObjectURL(file));
    };

    const clearLightLogo = () => {
        setLightFile(null);
        setLightPreview(null);
        setLightAction(isEdit ? 'clear' : 'unchanged');
        if (lightInputRef.current) lightInputRef.current.value = '';
    };

    const clearDarkLogo = () => {
        setDarkFile(null);
        setDarkPreview(null);
        setDarkAction(isEdit ? 'clear' : 'unchanged');
        if (darkInputRef.current) darkInputRef.current.value = '';
    };

    const buildThemeBrandLogosPayload = async (): Promise<Record<string, ThemeLogoSlotPayload | null> | undefined> => {
        const out: Record<string, ThemeLogoSlotPayload | null> = {};
        if (isEdit) {
            if (lightAction === 'clear') out.light = null;
            else if (lightAction === 'replace' && lightFile) {
                out.light = { base64: await readFileAsBase64(lightFile), contentType: lightFile.type || 'image/png' };
            }
            if (darkAction === 'clear') out.dark = null;
            else if (darkAction === 'replace' && darkFile) {
                out.dark = { base64: await readFileAsBase64(darkFile), contentType: darkFile.type || 'image/png' };
            }
        } else {
            if (lightFile) {
                out.light = { base64: await readFileAsBase64(lightFile), contentType: lightFile.type || 'image/png' };
            }
            if (darkFile) {
                out.dark = { base64: await readFileAsBase64(darkFile), contentType: darkFile.type || 'image/png' };
            }
        }
        return Object.keys(out).length ? out : undefined;
    };

    const handleSave = async () => {
        if (!form.domainKey.trim()) { setError('Domain Key is required'); return; }
        if (!form.apiBaseUrl.trim()) { setError('API Base URL is required'); return; }
        if (!form.menuKey.trim()) { setError('Menu Key is required'); return; }
        if (!form.brandLogoName.trim() || !form.brandLogoPath.trim()) { setError('Brand Logo name and path are required'); return; }
        if (!form.minifiedBrandLogoName.trim() || !form.minifiedBrandLogoPath.trim()) { setError('Minified Brand Logo name and path are required'); return; }

        if (form.ssoSignUpEnabled) {
            if (!form.ssoSignUpLicenseType.trim()) { setError('License Type is required when SSO signup is enabled'); return; }
            if (!form.ssoSignUpModule.trim()) { setError('Module is required when SSO signup is enabled'); return; }
            if (!form.ssoSignUpDuration.trim()) { setError('Duration is required when SSO signup is enabled'); return; }
        }

        const anyInfralytiqsField = [form.infralytiqsTenantId, form.infralytiqsSiteId, form.infralytiqsDbName]
            .some(v => v.trim());
        if (anyInfralytiqsField) {
            if (!form.infralytiqsTenantId.trim() || !form.infralytiqsSiteId.trim() || !form.infralytiqsDbName.trim()) {
                setError('Infralytiqs: Tenant ID, Site ID, and DB Name must all be provided together');
                return;
            }
            if (!INFRALYTIQS_DB_NAME_RE.test(form.infralytiqsDbName.trim())) {
                setError('Infralytiqs DB Name must start with a letter and contain only letters, digits, and underscores (max 63 chars)');
                return;
            }
        }

        setSaving(true);
        setError('');

        let themeBrandLogos: Record<string, ThemeLogoSlotPayload | null> | undefined;
        try {
            themeBrandLogos = await buildThemeBrandLogosPayload();
        } catch (fe: any) {
            setSaving(false);
            setError(fe?.message || 'Failed to read logo file');
            return;
        }

        const payload = {
            domainKey: form.domainKey.trim(),
            apiBaseUrl: form.apiBaseUrl.trim(),
            brandLogo: { name: form.brandLogoName.trim(), path: form.brandLogoPath.trim() },
            minifiedBrandLogo: { name: form.minifiedBrandLogoName.trim(), path: form.minifiedBrandLogoPath.trim() },
            ...(themeBrandLogos ? { themeBrandLogos } : {}),
            menuKey: form.menuKey.trim(),
            theme: form.theme === 'professional' ? 'professional' : 'DEFAULT',
            ...((
                form.uiDictionaryKey === 'campaignKing'
                || form.uiDictionaryKey === 'instantUpdate'
                || form.uiDictionaryKey === 'anamnesis'
                || form.uiDictionaryKey === 'infralytiqs'
            ) ? { uiDictionaryKey: form.uiDictionaryKey }
                : isEdit ? { uiDictionaryKey: '' } : {}),
            supportEmail: form.supportEmail.trim(),
            contactPhone: form.contactPhone.trim(),
            contactEmail: form.contactEmail.trim(),
            logLevel: form.logLevel,
            facebookAppId: form.facebookAppId.trim(),
            facebookApiVersion: form.facebookApiVersion.trim(),
            fbEmbeddedRedirectUri: form.fbEmbeddedRedirectUri.trim(),
            googleMapsBrowserJSApiKey: form.googleMapsBrowserJSApiKey.trim(),
            loginPageHashPath: form.loginPageHashPath.trim(),
            loginSignInTermsNotice: quillHtmlToStored(form.loginSignInTermsNotice),
            userSignUpEnabled: form.userSignUpEnabled,
            ssoSignUpEnabled: form.ssoSignUpEnabled,
            ssoSignUp: {
                licenseType: form.ssoSignUpLicenseType.trim(),
                module: form.ssoSignUpModule.trim(),
                duration: form.ssoSignUpDuration.trim(),
            },
            managePlan: form.managePlan,
            assetsS3CdnUrl: form.assetsS3CdnUrl.trim(),
            infralytiqs: {
                tenantId: form.infralytiqsTenantId.trim(),
                siteId: form.infralytiqsSiteId.trim(),
                dbName: form.infralytiqsDbName.trim(),
                captureLocation: form.infralytiqsCaptureLocation,
            },
            beamOnScreenInfralytiqs: {
                tenantId: form.beamOnScreenTenantId.trim(),
                siteId: form.beamOnScreenSiteId.trim(),
            },
            ssoProviders: ssoProviders.filter(p => p.id.trim()).map(p => ({
                id: p.id.trim(),
                buttonName: p.buttonName.trim(),
                providerType: p.providerType,
                loginPageHashPath: p.loginPageHashPath.trim() || '/#/login',
                clientId: p.clientId.trim(),
                clientSecret: p.clientSecret.trim(),
                scopes: p.scopes.trim() || 'openid email profile',
                authorizationUrl: p.authorizationUrl.trim(),
                tokenUrl: p.tokenUrl.trim(),
                ...(p.prompt.trim() ? { prompt: p.prompt.trim() } : {}),
            })),
        };

        try {
            if (isEdit) {
                await apiClient.put(`/domain/${domain._id}`, payload);
            } else {
                await apiClient.post('/domain/new', payload);
            }
            onSave();
        } catch (err: any) {
            const msg = err?.data?.message || err?.message || 'Failed to save domain';
            setError(msg);
            Logger.error('DomainDialog', `Save error: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Domain' : 'Create Domain'}</DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>
                )}

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Core Settings</Typography>
                <Box display="flex" flexDirection="column" gap={2} mb={2}>
                    <TextField label="Domain Key" value={form.domainKey} onChange={handleChange('domainKey')}
                        size="small" fullWidth required placeholder="e.g. my-local.campaignking.ai" />
                    <TextField label="API Base URL" value={form.apiBaseUrl} onChange={handleChange('apiBaseUrl')}
                        size="small" fullWidth required placeholder="e.g. http://localhost:8080" />
                    <TextField
                        label="Assets S3 CDN URL"
                        value={form.assetsS3CdnUrl}
                        onChange={handleChange('assetsS3CdnUrl')}
                        size="small"
                        fullWidth
                        placeholder="https://assets.campaignking.ai"
                        helperText="Published Asset Explorer CDN origin. Leave empty to use CLIENT_DOMAIN_CONFIGS / ASSETS_S3_CDN_URL / default."
                    />
                    <Box display="flex" gap={2}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Menu Key</InputLabel>
                            <Select value={form.menuKey} label="Menu Key" onChange={handleSelectChange('menuKey')}>
                                <MenuItem value="campaignKing">campaignKing</MenuItem>
                                <MenuItem value="instantUpdate">instantUpdate</MenuItem>
                                <MenuItem value="anamnesis">anamnesis</MenuItem>
                                <MenuItem value="infralytiqs">infralytiqs</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>UI labels dictionary</InputLabel>
                            <Select
                                value={
                                    form.uiDictionaryKey === 'campaignKing'
                                    || form.uiDictionaryKey === 'instantUpdate'
                                    || form.uiDictionaryKey === 'anamnesis'
                                    || form.uiDictionaryKey === 'infralytiqs'
                                        ? form.uiDictionaryKey
                                        : ''
                                }
                                label="UI labels dictionary"
                                onChange={handleSelectChange('uiDictionaryKey')}
                            >
                                <MenuItem value="">Follow Menu Key</MenuItem>
                                <MenuItem value="campaignKing">Campaign King wording</MenuItem>
                                <MenuItem value="instantUpdate">Instant Update wording</MenuItem>
                                <MenuItem value="anamnesis">Anamnesis wording</MenuItem>
                                <MenuItem value="infralytiqs">Infralytiqs wording</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Theme</InputLabel>
                            <Select value={form.theme} label="Theme" onChange={handleSelectChange('theme')}>
                                <MenuItem value="DEFAULT">Default (Light / Dark)</MenuItem>
                                <MenuItem value="professional">Professional</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Log Level</InputLabel>
                            <Select value={form.logLevel} label="Log Level" onChange={handleSelectChange('logLevel')}>
                                <MenuItem value="debug">debug</MenuItem>
                                <MenuItem value="info">info</MenuItem>
                                <MenuItem value="warn">warn</MenuItem>
                                <MenuItem value="error">error</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Login Page Hash Path" value={form.loginPageHashPath}
                            onChange={handleChange('loginPageHashPath')} size="small" fullWidth placeholder="/#/login" />
                    </Box>
                    <FormControlLabel
                        control={(
                            <Switch
                                color="secondary"
                                checked={form.userSignUpEnabled}
                                onChange={(_, checked) => setForm(prev => ({ ...prev, userSignUpEnabled: checked }))}
                            />
                        )}
                        label="Allow User Sign Up on login page"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
                        When off, only Sign In is shown; <code>mode=signup</code> in the URL is ignored.
                    </Typography>
                    <FormControlLabel
                        control={(
                            <Switch
                                color="secondary"
                                checked={form.ssoSignUpEnabled}
                                onChange={(_, checked) => setForm(prev => ({ ...prev, ssoSignUpEnabled: checked }))}
                            />
                        )}
                        label="Enable signup via SSO Login"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
                        When on, a first-time SSO login auto-creates a company and admin user using the License Type, Module, and Duration below.
                    </Typography>
                    {form.ssoSignUpEnabled && (
                        <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 180 }} required>
                                <InputLabel>License Type</InputLabel>
                                <Select
                                    value={form.ssoSignUpLicenseType}
                                    label="License Type"
                                    onChange={handleSelectChange('ssoSignUpLicenseType')}
                                >
                                    {LICENSE_TYPES.map((option) => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 200 }} required>
                                <InputLabel>Module</InputLabel>
                                <Select
                                    value={form.ssoSignUpModule}
                                    label="Module"
                                    onChange={handleSelectChange('ssoSignUpModule')}
                                    disabled={modulesLoading}
                                >
                                    {availableModules.map((option) => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 160 }} required>
                                <InputLabel>Duration</InputLabel>
                                <Select
                                    value={form.ssoSignUpDuration}
                                    label="Duration"
                                    onChange={handleSelectChange('ssoSignUpDuration')}
                                >
                                    {SSO_SIGNUP_DURATIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                    <FormControlLabel
                        control={(
                            <Switch
                                color="secondary"
                                checked={form.managePlan}
                                onChange={(_, checked) => setForm(prev => ({ ...prev, managePlan: checked }))}
                            />
                        )}
                        label="Show Manage Plan menu"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
                        Controls visibility of the “Manage Plan” sidebar menu for this domain. When off, it is hidden.
                    </Typography>
                    <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Login page — Sign In terms notice
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Rich text; returned by <code>/system/client/config</code> for the login screen. Leave empty for the built-in default under “Terms and Conditions”.
                        </Typography>
                        {loginTermsEditorReady && (
                            <Box
                                sx={{
                                    '& .ql-toolbar': { borderTopLeftRadius: 4, borderTopRightRadius: 4 },
                                    '& .ql-container': {
                                        minHeight: 100,
                                        fontSize: '0.875rem',
                                        borderBottomLeftRadius: 4,
                                        borderBottomRightRadius: 4,
                                    },
                                    '& .ql-editor': { minHeight: 100 },
                                }}
                            >
                                <QuillWrapper
                                    theme="snow"
                                    value={form.loginSignInTermsNotice}
                                    onChange={(html) => setForm(prev => ({ ...prev, loginSignInTermsNotice: html }))}
                                    modules={loginTermsQuillModules}
                                    formats={loginTermsQuillFormats}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Brand Logo</Typography>
                <Box display="flex" gap={2} mb={2}>
                    <TextField label="Logo Component Name" value={form.brandLogoName}
                        onChange={handleChange('brandLogoName')} size="small" fullWidth required placeholder="e.g. CKSiteLogo" />
                    <TextField label="Logo Component Path" value={form.brandLogoPath}
                        onChange={handleChange('brandLogoPath')} size="small" fullWidth required placeholder="e.g. src/components/SiteLogo/CKSiteLogo.tsx" />
                </Box>
                <Box display="flex" gap={2} mb={2}>
                    <TextField label="Minified Logo Name" value={form.minifiedBrandLogoName}
                        onChange={handleChange('minifiedBrandLogoName')} size="small" fullWidth required placeholder="e.g. MinifiedCKSiteLogo" />
                    <TextField label="Minified Logo Path" value={form.minifiedBrandLogoPath}
                        onChange={handleChange('minifiedBrandLogoPath')} size="small" fullWidth required placeholder="e.g. src/components/SiteLogo/MinifiedCKSiteLogo.tsx" />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Theme image logos (optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    When set, these images replace the TypeScript logos on the login page and dashboard header for the matching theme (light / dark). Stored in MongoDB and returned by <code>/system/client/config</code>.
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={3} mb={2}>
                    <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Light theme</Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mb={1}>
                            <Button size="small" variant="outlined" color="secondary" component="label" htmlFor="domain-logo-light-input">
                                Choose file
                            </Button>
                            <input id="domain-logo-light-input" ref={lightInputRef} type="file" accept={ACCEPT_IMAGE} hidden onChange={onPickLight} />
                            {lightPreview && (
                                <Button size="small" color="inherit" onClick={clearLightLogo}>Remove</Button>
                            )}
                        </Box>
                        {lightPreview && (
                            <Box component="img" src={lightPreview} alt="" sx={{ maxHeight: 56, maxWidth: '100%', objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }} />
                        )}
                    </Box>
                    <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Dark theme</Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mb={1}>
                            <Button size="small" variant="outlined" color="secondary" component="label" htmlFor="domain-logo-dark-input">
                                Choose file
                            </Button>
                            <input id="domain-logo-dark-input" ref={darkInputRef} type="file" accept={ACCEPT_IMAGE} hidden onChange={onPickDark} />
                            {darkPreview && (
                                <Button size="small" color="inherit" onClick={clearDarkLogo}>Remove</Button>
                            )}
                        </Box>
                        {darkPreview && (
                            <Box component="img" src={darkPreview} alt="" sx={{ maxHeight: 56, maxWidth: '100%', objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }} />
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Contact Details</Typography>
                <Box display="flex" gap={2} mb={2}>
                    <TextField label="Support Email" value={form.supportEmail}
                        onChange={handleChange('supportEmail')} size="small" fullWidth />
                    <TextField label="Contact Email" value={form.contactEmail}
                        onChange={handleChange('contactEmail')} size="small" fullWidth />
                    <TextField label="Contact Phone" value={form.contactPhone}
                        onChange={handleChange('contactPhone')} size="small" fullWidth />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Third-Party</Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2}>
                        <TextField label="Facebook App ID" value={form.facebookAppId}
                            onChange={handleChange('facebookAppId')} size="small" fullWidth />
                        <TextField label="Facebook API Version" value={form.facebookApiVersion}
                            onChange={handleChange('facebookApiVersion')} size="small" fullWidth placeholder="e.g. v23.0" />
                    </Box>
                    <TextField label="FB Embedded Redirect URI" value={form.fbEmbeddedRedirectUri}
                        onChange={handleChange('fbEmbeddedRedirectUri')} size="small" fullWidth />
                    <TextField label="Google Maps Browser JS API Key" value={form.googleMapsBrowserJSApiKey}
                        onChange={handleChange('googleMapsBrowserJSApiKey')} size="small" fullWidth />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Infralytiqs Tracker Bootstrap
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    These values are returned publicly by <code>/system/client/config?domainKey=...</code> so
                    the browser-side Infralytiqs SDK can initialise itself. They must match a ClickHouse
                    module configuration in IL-Settings (Tenant ID + Site ID + DB Name).
                </Typography>
                <Box display="flex" gap={2} mb={2}>
                    <TextField
                        label="Tenant ID"
                        value={form.infralytiqsTenantId}
                        onChange={handleChange('infralytiqsTenantId')}
                        size="small"
                        fullWidth
                        placeholder="e.g. campaignking"
                    />
                    <TextField
                        label="Site ID"
                        value={form.infralytiqsSiteId}
                        onChange={handleChange('infralytiqsSiteId')}
                        size="small"
                        fullWidth
                        placeholder="e.g. my-local-campaignking-ai"
                    />
                    <TextField
                        label="DB Name"
                        value={form.infralytiqsDbName}
                        onChange={handleChange('infralytiqsDbName')}
                        size="small"
                        fullWidth
                        placeholder="e.g. analytics_campaignking_local"
                        inputProps={{ pattern: '^[a-zA-Z][a-zA-Z0-9_]{0,62}$' }}
                    />
                </Box>
                <Box sx={{ mb: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.infralytiqsCaptureLocation}
                                onChange={(_, checked) =>
                                    setForm((prev) => ({ ...prev, infralytiqsCaptureLocation: checked }))
                                }
                                color="secondary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">
                                    Capture precise visitor location (opt-in)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    When enabled, the browser SDK prompts visitors for geolocation
                                    permission on load. Granted visitors are plotted as precise
                                    points on the heatmap. When disabled, the heatmap falls back to
                                    country-level centroids from server-side IP geolocation (no
                                    consent required).
                                </Typography>
                            </Box>
                        }
                        sx={{ alignItems: 'flex-start' }}
                    />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Beam on Screen playback
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Tenant and site for screen playback events. Create a ClickHouse module configuration in
                    Infralytiqs Settings with the same Tenant ID and Site ID. That configuration's database
                    is where the plays are stored.
                </Typography>
                <Box display="flex" gap={2} mb={2}>
                    <TextField
                        label="Tenant ID"
                        value={form.beamOnScreenTenantId}
                        onChange={handleChange('beamOnScreenTenantId')}
                        size="small"
                        fullWidth
                        placeholder="beamOnScreen"
                    />
                    <TextField
                        label="Site ID"
                        value={form.beamOnScreenSiteId}
                        onChange={handleChange('beamOnScreenSiteId')}
                        size="small"
                        fullWidth
                        placeholder="screenPlayBacks"
                    />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" color="text.secondary">SSO Providers</Typography>
                    <IconButton size="small" color="secondary" onClick={addSsoProvider} title="Add SSO Provider">
                        <AddCircleOutlineIcon />
                    </IconButton>
                </Box>
                {ssoProviders.map((prov, idx) => (
                    <Box key={idx} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2, position: 'relative' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Provider #{idx + 1}
                            </Typography>
                            <IconButton size="small" color="error" onClick={() => removeSsoProvider(idx)} title="Remove">
                                <RemoveCircleOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Box display="flex" gap={2} mb={1}>
                            <TextField label="ID" value={prov.id} onChange={handleSsoChange(idx, 'id')}
                                size="small" fullWidth required placeholder="e.g. google-ck-local" />
                            <TextField label="Button Name" value={prov.buttonName} onChange={handleSsoChange(idx, 'buttonName')}
                                size="small" fullWidth required placeholder="e.g. Sign in with Google" />
                        </Box>
                        <Box display="flex" gap={2} mb={1}>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Provider Type</InputLabel>
                                <Select value={prov.providerType} label="Provider Type" onChange={handleSsoSelectChange(idx, 'providerType')}>
                                    <MenuItem value="google">google</MenuItem>
                                    <MenuItem value="microsoft">microsoft</MenuItem>
                                    <MenuItem value="oidc">oidc</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField label="Login Hash Path" value={prov.loginPageHashPath} onChange={handleSsoChange(idx, 'loginPageHashPath')}
                                size="small" fullWidth placeholder="/#/login" />
                            <TextField label="Prompt" value={prov.prompt} onChange={handleSsoChange(idx, 'prompt')}
                                size="small" fullWidth placeholder="e.g. select_account" />
                        </Box>
                        <Box display="flex" gap={2} mb={1}>
                            <TextField label="Client ID" value={prov.clientId} onChange={handleSsoChange(idx, 'clientId')}
                                size="small" fullWidth required />
                            <TextField label="Client Secret" value={prov.clientSecret} onChange={handleSsoChange(idx, 'clientSecret')}
                                size="small" fullWidth required type="password" />
                        </Box>
                        <TextField label="Scopes" value={prov.scopes} onChange={handleSsoChange(idx, 'scopes')}
                            size="small" fullWidth sx={{ mb: 1 }} placeholder="openid email profile" />
                        <Box display="flex" gap={2}>
                            <TextField label="Authorization URL" value={prov.authorizationUrl} onChange={handleSsoChange(idx, 'authorizationUrl')}
                                size="small" fullWidth required />
                            <TextField label="Token URL" value={prov.tokenUrl} onChange={handleSsoChange(idx, 'tokenUrl')}
                                size="small" fullWidth required />
                        </Box>
                    </Box>
                ))}
                {ssoProviders.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', ml: 1 }}>
                        No SSO providers configured. Click + to add one.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" color="secondary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
